// src/pages/Events/components/BookmarkManager.jsx
// 🔖 Bookmark management with notes

import { useState } from "react";
import { FiBookmark, FiTrash2, FiEdit, FiClock, FiArrowRight } from "react-icons/fi";
import { useTheme } from "../../../context/ThemeContext";

export default function BookmarkManager({
  bookmarks = [],
  onJumpTo,
  onDelete,
  onEdit,
}) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState("");

  const startEdit = (bookmark) => {
    setEditingId(bookmark.id);
    setEditNote(bookmark.note || "");
  };

  const saveEdit = () => {
    if (onEdit && editingId) {
      onEdit(editingId, editNote);
      setEditingId(null);
      setEditNote("");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditNote("");
  };

  return (
    <div className="p-4 space-y-4">
      {/* ===== HEADER ===== */}
      <div>
        <h2 className="text-lg font-bold mb-1 flex items-center gap-2">
          <FiBookmark className="w-5 h-5" />
          Bookmarks
        </h2>
        <p className="text-xs text-gray-500">
          {bookmarks.length} bookmark{bookmarks.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* ===== BOOKMARK LIST ===== */}
      {bookmarks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FiBookmark className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <div className="text-sm">No bookmarks yet</div>
          <div className="text-xs mt-1">
            Click the bookmark button to save important moments
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {bookmarks.map(bookmark => (
            <div
              key={bookmark.id}
              className={`p-3 rounded-lg border ${
                isDark
                  ? "bg-neutral-800 border-neutral-700"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Time */}
              <div className="flex items-center gap-2 mb-2">
                <FiClock className="w-4 h-4 text-gray-500" />
                <span className="font-mono text-sm font-medium">
                  {new Date(bookmark.timestamp).toLocaleTimeString()}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(bookmark.timestamp).toLocaleDateString()}
                </span>
              </div>

              {/* Note */}
              {editingId === bookmark.id ? (
                <div className="space-y-2">
                  <textarea
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    className={`w-full px-2 py-1 rounded border text-sm ${
                      isDark
                        ? "bg-neutral-900 border-neutral-600"
                        : "bg-gray-50 border-gray-300"
                    }`}
                    rows={2}
                    placeholder="Add a note..."
                    autoFocus
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={saveEdit}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className={`px-3 py-1 rounded text-xs ${
                        isDark
                          ? "bg-neutral-700 hover:bg-neutral-600"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {bookmark.note || <em className="text-gray-500">No note</em>}
                </div>
              )}

              {/* Actions */}
              {editingId !== bookmark.id && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onJumpTo(bookmark)}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                  >
                    <FiArrowRight className="w-3 h-3" />
                    Jump To
                  </button>

                  {onEdit && (
                    <button
                      onClick={() => startEdit(bookmark)}
                      className={`p-1 rounded text-xs ${
                        isDark
                          ? "hover:bg-neutral-700"
                          : "hover:bg-gray-200"
                      }`}
                      title="Edit note"
                    >
                      <FiEdit className="w-4 h-4" />
                    </button>
                  )}

                  <button
                    onClick={() => {
                      if (window.confirm("Delete this bookmark?")) {
                        onDelete(bookmark.id);
                      }
                    }}
                    className={`p-1 rounded text-xs text-red-600 ${
                      isDark
                        ? "hover:bg-red-900/20"
                        : "hover:bg-red-100"
                    }`}
                    title="Delete bookmark"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ===== TIPS ===== */}
      {bookmarks.length > 0 && (
        <div className={`p-3 rounded text-xs ${
          isDark ? "bg-blue-900/20 text-blue-400" : "bg-blue-50 text-blue-700"
        }`}>
          💡 Tip: Bookmarks help you quickly return to important moments for review or export
        </div>
      )}
    </div>
  );
}
